import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface PrivatePerson {
  id: string;
  firstName: string;
  lastName: string;
  sex?: string;
  nationality?: string;
  socialSecurityNumber?: string;
  contactPhone?: string;
  contactEmail?: string;
  bornDate?: string;
  clientId?: string;
  createdAt?: string;
  updatedAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PrivatePersonService {
  private apiUrl = '/api/v1/private-persons';

  constructor(private http: HttpClient) {}

  getPrivatePersons(): Observable<PrivatePerson[]> {
    return this.http.get<PrivatePerson[]>(this.apiUrl);
  }

  getPrivatePersonsByClient(clientId: string): Observable<PrivatePerson[]> {
    return this.http.get<PrivatePerson[]>(`${this.apiUrl}/client/${clientId}`);
  }

  getPrivatePersonById(id: string): Observable<PrivatePerson> {
    return this.http.get<PrivatePerson>(`${this.apiUrl}/${id}`);
  }

  createPrivatePerson(person: Omit<PrivatePerson, 'id'>): Observable<PrivatePerson> {
    return this.http.post<PrivatePerson>(this.apiUrl, person);
  }

  updatePrivatePerson(id: string, person: Partial<PrivatePerson>): Observable<PrivatePerson> {
    return this.http.put<PrivatePerson>(`${this.apiUrl}/${id}`, person);
  }

  deletePrivatePerson(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  // Search methods
  getByFirstName(firstName: string): Observable<PrivatePerson[]> {
    return this.http.get<PrivatePerson[]>(`${this.apiUrl}/first-name/${firstName}`);
  }

  getByLastName(lastName: string): Observable<PrivatePerson[]> {
    return this.http.get<PrivatePerson[]>(`${this.apiUrl}/last-name/${lastName}`);
  }

  getByFullName(fullName: string): Observable<PrivatePerson[]> {
    return this.http.get<PrivatePerson[]>(`${this.apiUrl}/full-name/${fullName}`);
  }

  getByEmail(email: string): Observable<PrivatePerson[]> {
    return this.http.get<PrivatePerson[]>(`${this.apiUrl}/email/${email}`);
  }

  getByPhone(phone: string): Observable<PrivatePerson[]> {
    return this.http.get<PrivatePerson[]>(`${this.apiUrl}/phone/${phone}`);
  }

  getBySocialSecurity(ssn: string): Observable<PrivatePerson[]> {
    return this.http.get<PrivatePerson[]>(`${this.apiUrl}/social-security/${ssn}`);
  }

  getByNationality(nationality: string): Observable<PrivatePerson[]> {
    return this.http.get<PrivatePerson[]>(`${this.apiUrl}/nationality/${nationality}`);
  }

  getBySex(sex: string): Observable<PrivatePerson[]> {
    return this.http.get<PrivatePerson[]>(`${this.apiUrl}/sex/${sex}`);
  }
}
